/**
 * Shared page container for ALL dashboard pages — both admin (`/admin/...`)
 * and agent CRM (`/crm/...`).
 *
 * Single source of truth for page-level content width and left/right gutters,
 * so every page has identical max-width + gutters page-to-page, matching the
 * canonical "Company Commissions" admin layout (issue #804, building on
 * #699/#731).
 *
 * The surrounding layout (`AdminLayout` / `CrmLayout`) already provides the
 * outer page padding (`p-4 sm:p-6`, per the style guide). This container only
 * constrains the content width and centres it (`max-w-[1400px] mx-auto` — the
 * Company Commissions treatment). Individual pages MUST NOT re-add their own
 * outer `max-w-*` / `mx-auto` / `p-4 sm:p-6` — they inherit it here so the
 * width + gutters never drift again.
 *
 * Responsive by design: a single fluid max-width + `p-4 sm:p-6` gutters in the
 * layout. No per-breakpoint width hacks or fixed pixel widths.
 *
 * `overflow-x-clip` prevents any child element from causing horizontal scroll
 * on mobile — grids, large numbers, and long text are clipped to the container
 * width (#865) — while leaving the Y-axis `visible` so a list page's sticky
 * table `<thead>` can pin against the scrolling <main> (#2577).
 *
 * Pages that need a narrower *content* column (e.g. a settings form) should use
 * an inner wrapper inside their own content, not a competing outer container.
 */
import type { ReactNode } from "react";

export function PageContainer({ children }: { children: ReactNode }) {
  // w-full so the container fills the padded <main> up to the max width, then
  // mx-auto centres it on very wide viewports — identical gutters everywhere.
  // overflow-hidden clamps child content to prevent mobile horizontal scroll.
  // #2577: `overflow-x-clip` (not `overflow-hidden`) still clamps child content
  // horizontally on mobile (#865) but leaves `overflow-y: visible`, so a
  // list page's sticky table `<thead>` can pin against the scrolling <main>
  // (a `hidden`/`auto` ancestor would trap the sticky context).
  return (
    <div className="mx-auto w-full max-w-[1400px] overflow-x-clip">{children}</div>
  );
}
